import { useState, useEffect } from 'react';
import { X, Clock, Sparkles, Target, Users, TrendingUp, Zap } from 'lucide-react';
import { ModalPortal } from './ModalPortal';
import { trackButtonClick } from '../../firebaseConfig';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';
import { Button } from './Button';

interface TimeLeft {
    hours: number;
    minutes: number;
    seconds: number;
}

interface WeekendCampaignModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function WeekendCampaignModal({ isOpen, onClose }: WeekendCampaignModalProps) {
    const [isWeekend, setIsWeekend] = useState(false);
    const [timeLeft, setTimeLeft] = useState<TimeLeft>({ hours: 0, minutes: 0, seconds: 0 });
    const { trackButtonClick: trackAnalyticsButton, trackConversion } = useAnalyticsTracking();

    // Check if current time is weekend and calculate countdown
    useEffect(() => {
        const updateWeekendStatus = () => {
            const now = new Date();
            const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
            const currentIsWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Saturday or Sunday

            setIsWeekend(currentIsWeekend);

            if (currentIsWeekend) {
                // Calculate time until Monday 12:00 AM
                const nextMonday = new Date(now);

                if (dayOfWeek === 6) {
                    // If Saturday, go to next Monday
                    nextMonday.setDate(now.getDate() + 2);
                } else {
                    // If Sunday, go to next day (Monday)
                    nextMonday.setDate(now.getDate() + 1);
                }

                nextMonday.setHours(0, 0, 0, 0); // Set to midnight

                const timeDiff = nextMonday.getTime() - now.getTime();

                if (timeDiff > 0) {
                    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
                    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

                    setTimeLeft({ hours, minutes, seconds });
                } else {
                    setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
                }
            }
        };

        if (isOpen) {
            updateWeekendStatus();
            const interval = setInterval(updateWeekendStatus, 1000);
            return () => clearInterval(interval);
        }
    }, [isOpen]);

    const handleDismiss = () => {
        // Save to localStorage to not show again
        localStorage.setItem('weekend-campaign-dismissed', 'true');

        // Analytics tracking
        trackAnalyticsButton('Dismiss Weekend Modal', 'Weekend Campaign Modal', {
            button_type: 'dismiss',
            button_position: 'modal_close',
            is_weekend: isWeekend
        });

        trackButtonClick('Dismiss Weekend Modal', 'Weekend Campaign Modal');
        onClose();
    };

    const handleCTAClick = () => {
        if (isWeekend) {
            // Analytics tracking for weekend
            trackAnalyticsButton('Start Practicing - Weekend Modal', 'Weekend Campaign Modal', {
                button_type: 'primary_cta',
                button_position: 'modal_cta',
                conversion_intent: 'free_weekend_usage',
                campaign_type: 'weekend_free_modal',
                is_weekend: true
            });

            // Track conversion
            trackConversion('weekend_free_signup', {
                source: 'weekend_modal',
                button_name: 'Start Practicing',
                campaign_type: 'weekend_free_modal'
            });

            // Firebase tracking
            trackButtonClick('Start Practicing - Weekend Modal', 'Weekend Campaign Modal');
        } else {
            // Analytics tracking for non-weekend
            trackAnalyticsButton('Sign Up Now - Weekend Modal', 'Weekend Campaign Modal', {
                button_type: 'primary_cta',
                button_position: 'modal_cta',
                conversion_intent: 'sign_up',
                campaign_type: 'weekend_preview_modal',
                is_weekend: false
            });

            // Track conversion
            trackConversion('sign_up', {
                source: 'weekend_modal',
                button_name: 'Sign Up Now',
                campaign_type: 'weekend_preview_modal'
            });

            // Firebase tracking
            trackButtonClick('Sign Up Now - Weekend Modal', 'Weekend Campaign Modal');
        }

        const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
        const campaign = isWeekend ? 'free_weekend_modal' : 'weekend_signup_modal';
        window.open(`${platformUrl}/login?utm_source=weekend_modal&utm_medium=modal&utm_campaign=${campaign}`, '_blank');

        // Close modal after action
        onClose();
    };

    const formatTime = (time: number) => {
        return time.toString().padStart(2, '0');
    };

    // Benefits data
    const weekendBenefits = [
        'Unlimited mock interviews',
        'Create your own interviews',
        'Practice, get feedback & improve',
        'No time limits or restrictions',
        'Read & Learn from Skill-based News',
        'Detailed feedback of performance'
    ];

    const regularBenefits = [
        'AI-powered interview coaching',
        'Real-time interview preparation',
        'No time limits or restrictions',
        'Free access every weekend',
        'Technical & non-technical interviews',
        'Detailed feedback of performance'
    ];

    const benefits = isWeekend ? weekendBenefits : regularBenefits;

    if (!isOpen) return null;

    return (
        <ModalPortal>
            <div className="modal-backdrop fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                <div className="relative max-w-xl w-full bg-background-light dark:bg-background-dark rounded-2xl shadow-2xl border border-border-light dark:border-border-dark overflow-hidden animate-scale-in">
                    {/* Close button */}
                    <button
                        onClick={handleDismiss}
                        className="absolute top-5 right-5 z-10 w-9 h-9 rounded-full bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm hover:bg-surface-light dark:hover:bg-surface-dark transition-all duration-200 flex items-center justify-center group"
                        aria-label="Close modal"
                    >
                        <X className="w-5 h-5 text-text-primary-light dark:text-text-primary-dark group-hover:text-text-primary-light dark:group-hover:text-text-primary-dark transition-colors" />
                    </button>

                    {/* Header with gradient background */}
                    <div className={`
            relative px-8 pt-8 pb-6
            ${isWeekend
                            ? 'bg-gradient-to-br from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark'
                            : 'bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark'
                        }
          `}>
                        {/* Animated background for weekend state */}
                        {isWeekend && (
                            <div className="absolute inset-0 bg-gradient-to-br from-primary-light/20 via-secondary-light/20 to-primary-light/20 dark:from-primary-dark/20 dark:via-secondary-dark/20 dark:to-primary-dark/20 animate-gradient bg-[length:200%_auto]" />
                        )}

                        {/* Secondary color accent for non-weekend */}
                        {!isWeekend && (
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-secondary-light/10 to-transparent dark:from-transparent dark:via-secondary-dark/10 dark:to-transparent" />
                        )}

                        {/* Shine effect for weekend state */}
                        {isWeekend && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shine" />
                        )}

                        <div className="relative text-center text-white">
                            <div className="flex items-center justify-center gap-2 mb-3">
                                <Sparkles className="w-6 h-6 animate-pulse" />
                                <span className="text-base font-bold">#FreeMockWeekends</span>
                                <Sparkles className="w-6 h-6 animate-pulse" />
                            </div>

                            <h2 className="text-2xl font-heading font-bold mb-3">
                                {isWeekend ? "It's Weekend! 🎉" : "Weekend Free Access"}
                            </h2>

                            <p className="text-base opacity-90 leading-relaxed">
                                MockTrail is <span className="font-bold">100% Free</span> every Saturday & Sunday
                            </p>

                            {/* Countdown timer for weekend */}
                            {isWeekend && (
                                <div className="mt-5 flex items-center justify-center gap-2 bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 mx-auto w-fit border border-white/20">
                                    <Clock className="w-5 h-5" />
                                    <span className="text-lg font-heading font-bold">
                                        {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {/* Benefits list */}
                        <div className="mb-8">
                            <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 text-center">
                                {isWeekend ? 'What you get this weekend:' : 'What you get with free weekends:'}
                            </h3>
                            <div className="grid grid-cols-1 gap-3">
                                {benefits.map((benefit, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-3 group"
                                    >
                                        <div className={`
                                            w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 bg-primary-light/15 dark:bg-primary-dark/15 group-hover:bg-primary-light/25 dark:group-hover:bg-secondary-dark/25
                                            }
                                        `}>
                                            <Sparkles className={`
                                                w-3 h-3 transition-colors text-primary-light dark:text-primary-dark
                                            `} />
                                        </div>
                                        <p className="text-sm text-text-primary-light dark:text-text-primary-dark font-medium">
                                            {benefit}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CTA Button */}
                        <Button
                            onClick={handleCTAClick}
                            className={`
                                w-full relative overflow-hidden group py-4 px-6 font-medium
                               
                            `}
                        >
                            <span className="relative flex items-center justify-center gap-2">
                                <Sparkles className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                                {isWeekend ? 'Start Practicing Now' : 'Sign Up for Free Weekends'}
                            </span>
                        </Button>

                        {/* Footer text */}
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center mt-4 leading-relaxed">
                            {isWeekend
                                ? 'No credit card required. Start practicing immediately!'
                                : 'Join thousands who practice free every weekend'
                            }
                        </p>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
}